import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

// @ts-ignore: need to upgrade or patch package
import { zodResolver } from '@hookform/resolvers/zod';
import { trackAction } from '@mint-lib/datadog';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { ClientType, useIsAgencyCompany } from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  CircularLoadingButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';

import { useAgencyClients } from '../../api/queries/agencyClients.js';
import { useUserRole } from '../../api/queries/userRoles.js';
import { useUsers } from '../../api/queries/usersData.js';
import {
  UserType,
  type Label,
  type UserStatus,
} from '../../api/users/index.js';
import useAlert from '../../hooks/useAlert/useAlert.jsx';
import useUserModalView from '../../hooks/useUserModalView/useUserModalView.jsx';
import type { OptionType } from '../../types/OptionType.js';
import { nullSafeFn } from '../../utils/index.js';
import { filterAgencyRoles } from './helpers.js';
import {
  HandleSubmitPramsType,
  type UserModalViewType,
} from './userModal.types.js';
import {
  UserEmailModalValidationSchema,
  UserModalType,
  UserModalValidationSchema,
} from './validation.js';

export const SUPPORTED_AVATAR_FORMATS = ['.jpeg', '.png', '.gif', '.jpg'];

const EMPTY_VALUES: UserModalType = {
  id: '',
  email: '',
  firstName: '',
  lastName: '',
  avatar: null,
  assignedClients: [],
  role: '',
  mobileNumber: '',
  officeNumber: '',
  isActive: true,
};

const nextStepMapping: Record<UserStatus, UserModalViewType> = {
  not_found: 'nonExistingUserView',
  exists: 'existingUserView',
  deactivated: 'emailView',
  exists_in_your_workspace: 'emailView',
};

type Props = {
  className?: string;
  onClose: () => void;
  onSubmit: (
    params: HandleSubmitPramsType,
  ) => Promise<UserType | null | undefined>;
  initialValues?: UserModalType;
  isActive: boolean;
  isReadonlyPermission: boolean;
  initialTags?: Label[] | null;
  isTagsLoading?: boolean;
  initialView?: UserModalViewType;
  /** Called before each email check request, e.g. to (re)start an operation. */
  onEmailCheckStart?: () => void;
  /** Called when the email check request fails. */
  onEmailCheckError?: () => void;
};

// TODO temporary move this helper to file because of imports problem
type GenericListType = { id: number; name: string };

const mapListToSelect = (list: GenericListType[]): OptionType[] =>
  list.map((option) => ({ value: option?.id, label: option?.name }));

const UserModalNew = ({
  onClose,
  onSubmit,
  initialValues = EMPTY_VALUES,
  initialTags = [],
  isTagsLoading = false,
  isActive,
  isReadonlyPermission,
  initialView = 'emailView',
  onEmailCheckStart,
  onEmailCheckError,
}: Props) => {
  const { t } = useTranslation('@myn/permissions');
  const ff = useFlags('@mint/arm', ['brandsAndGeoToMdm']);

  const isAgencyCompany = useIsAgencyCompany();

  const { agencyClients } = useAgencyClients();
  const { checkUserByEmail, checkUserByEmailNew } = useUsers({
    disableUsersFetch: true,
  });

  const [assignedClients, setAssignedClients] = useState<number[] | null>(null);

  const [currentView, setCurrentView] =
    useState<UserModalViewType>(initialView);

  const [isUserExist, setIsUserExist] = useState<boolean | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const {
    allRoles,
    userRole: { isLoading: userRoleLoading },
  } = useUserRole({ getFullList: true });

  const roleOptions = useMemo(
    () =>
      mapListToSelect(
        filterAgencyRoles(
          allRoles.data || [],
          initialValues.agencyUser ?? false,
        ),
      ),
    [allRoles.data],
  );

  const clientsOptions = useMemo(
    () =>
      nullSafeFn<ClientType[] | null, OptionType[]>(
        mapListToSelect,
        agencyClients,
      ),
    [agencyClients],
  );

  const isLoading = userRoleLoading || !roleOptions?.length;

  const hasInitialValues = useMemo(
    () => Object.keys(initialValues).length > 0,
    [initialValues],
  );

  const defaultValues: UserModalType = useMemo(() => {
    const initialRole = roleOptions?.find(
      (item) => item.label === initialValues.role,
    )?.value;

    // To keep is dirty state for the form we should pass all the values
    const values = hasInitialValues
      ? {
          ...initialValues,
          tags: initialTags?.map((tag) => String(tag.id)) || [],
        }
      : EMPTY_VALUES;

    return { ...values, role: initialRole || '' };
  }, [initialValues, roleOptions, initialTags]);

  const formInstance = useForm({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(
      currentView === 'emailView'
        ? UserEmailModalValidationSchema(t)
        : UserModalValidationSchema(t),
    ),
  });

  formInstance.watch();

  const handleEmailChange = useCallback(
    (email: string) => {
      setIsSaving(true);
      if (formInstance.formState.errors?.email || !email) {
        setIsUserExist(null);
        setIsSaving(false);
        return;
      }

      onEmailCheckStart?.();
      checkUserByEmailNew.mutateAsync(email, {
        onSuccess: (data) => {
          setIsUserExist(true);

          formInstance.reset({ ...EMPTY_VALUES, ...data.user, email });

          if (data.status) {
            setCurrentView(nextStepMapping[data.status]);
          }

          if (data.status === 'exists_in_your_workspace') {
            formInstance.setError('email', {
              type: 'custom',
              message: t(`This user is already in the workspace.`),
            });
          }

          if (data.status === 'deactivated') {
            // @ts-expect-error TODO: fix the type of form
            formInstance.setError('isActive', {
              type: 'custom',
              message: 'deactivated',
            });
          }
        },
        onError: () => {
          onEmailCheckError?.();
          enqueueSnackbar({
            variant: 'error',
            title: t('Error'),
            textLayout: 'multiline',
            subTitle: t(
              'Something went wrong while checking the user. Please try again.',
            ),
          });
        },
      });
      setIsSaving(false);
    },
    [checkUserByEmail, formInstance, onEmailCheckStart, onEmailCheckError],
  );

  const handleSubmitForm: SubmitHandler<UserModalType> = useCallback(
    (values) => {
      setIsSaving(true);
      const {
        firstName,
        lastName,
        email,
        mobileNumber,
        officeNumber,
        role,
        tags,
      } = values;

      if (!role || !email) {
        return;
      }

      trackAction('User management: save user', {
        isNewUser: !hasInitialValues,
        tagCount: tags?.length ?? 0,
      });

      onSubmit({
        userData: {
          firstName,
          lastName,
          email,
          mobileNumber,
          officeNumber,
          role,
          ...(ff.brandsAndGeoToMdm.enabled ? { tagsV2: tags || [] } : {}),
        },
        isUserExist: !!isUserExist,
        clients: assignedClients ?? [],
      }).then(() => {
        setIsSaving(false);
      });
    },
    [
      assignedClients,
      hasInitialValues,
      isUserExist,
      onSubmit,
      ff.brandsAndGeoToMdm.enabled,
    ],
  );

  const handleSubmitButtonClick = useCallback(() => {
    if (isReadonlyPermission) {
      onClose();
      return;
    }

    if (currentView === 'emailView' && formInstance.getValues('email')) {
      trackAction('User management: submit user email');
      formInstance.handleSubmit((data) => handleEmailChange(data.email))();
      return;
    }
    // @ts-expect-error TODO: fix the type of form
    formInstance.handleSubmit(handleSubmitForm)();
  }, [
    currentView,
    formInstance,
    handleEmailChange,
    handleSubmitForm,
    isReadonlyPermission,
  ]);

  // @ts-expect-error TODO: fix the type of form
  const alert = useAlert({ currentView, formInstance, hasInitialValues });

  const view = useUserModalView({
    currentView,
    // @ts-expect-error TODO: fix the type of form
    formInstance,
    isReadonlyPermission,
    roleOptions,
    clientsOptions,
    assignedClients,
    setAssignedClients,
    isLoading,
    isSaving,
    initialTags,
    isTagsLoading,
  });

  useEffect(() => {
    setAssignedClients(initialValues.assignedClients || null);
    formInstance.setValue(
      // @ts-expect-error TODO: fix the type of form
      'assignedClients',
      initialValues.assignedClients || [],
    );
  }, [initialValues.assignedClients, formInstance]);

  useEffect(() => {
    if (hasInitialValues) {
      formInstance.reset(defaultValues);
    }
  }, [hasInitialValues, defaultValues]);

  const isFieldsChanged =
    currentView === 'emailView'
      ? !formInstance.getValues('email') ||
        !!formInstance.formState.errors?.email
      : !!Object.keys(formInstance.formState.errors)?.length ||
        !formInstance.formState.isDirty ||
        (currentView === 'nonExistingUserView' &&
          !Object.keys(formInstance.formState.dirtyFields).length);

  // Saving before the labels arrive would submit an empty tagsV2 and wipe them.
  const isSaveDisabled =
    isTagsLoading || (!isReadonlyPermission && isFieldsChanged);

  const submitButtonLabel = () => {
    if (currentView === 'emailView') {
      return t('Next');
    }
    return isReadonlyPermission ? t('Close') : t('Save');
  };

  // Mirrors handleSubmitButtonClick: read-only users only close the modal.
  const getSubmitAction = () => {
    if (isReadonlyPermission) {
      return 'close';
    }
    return currentView === 'emailView' ? 'next' : 'save';
  };
  const submitAction = getSubmitAction();

  const submitButtonProps = {
    variant: 'contained',
    color: 'primary',
    size: 'large',
    onClick: handleSubmitButtonClick,
    disabled: isSaveDisabled || isSaving,
    'aria-label': 'save user data',
  } as const;

  const submitButtonContent = (
    <>
      {checkUserByEmail.isPending || isSaving ? (
        <CircularLoadingButton size="small" variant="contained" />
      ) : null}
      {submitButtonLabel()}
    </>
  );

  const modalTitle = () => {
    if (isReadonlyPermission) {
      return t(`User settings`);
    }
    return hasInitialValues ? t('Edit user') : t('New user');
  };

  return (
    <FormProvider {...formInstance}>
      <Dialog
        open={isActive}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <DialogPortal>
          <DialogOverlay data-dd-action-name="User modal: close" />
          <DialogRoot
            size={
              initialView === 'editUserView' && !isAgencyCompany ? 'lg' : 's'
            }
          >
            <DialogHeader
              onClose={onClose}
              data-dd-action-name="User modal: close"
            >
              <DialogTitle>{modalTitle()}</DialogTitle>
            </DialogHeader>
            <DialogContent className="flex flex-col gap-4">
              {alert}
              {view}
            </DialogContent>
            <DialogFooter>
              {isReadonlyPermission ? null : (
                <Button
                  variant="text"
                  size="large"
                  onClick={onClose}
                  data-dd-action-name="User modal: cancel"
                >
                  {t('Cancel')}
                </Button>
              )}
              {/* One button per action, so each reports its own action name. */}
              {submitAction === 'close' ? (
                <Button
                  {...submitButtonProps}
                  data-dd-action-name="User modal: close"
                >
                  {submitButtonContent}
                </Button>
              ) : null}
              {submitAction === 'next' ? (
                <Button
                  {...submitButtonProps}
                  data-dd-action-name="User modal: next"
                >
                  {submitButtonContent}
                </Button>
              ) : null}
              {submitAction === 'save' ? (
                <Button
                  {...submitButtonProps}
                  data-dd-action-name="User modal: save"
                >
                  {submitButtonContent}
                </Button>
              ) : null}
            </DialogFooter>
          </DialogRoot>
        </DialogPortal>
      </Dialog>
    </FormProvider>
  );
};

export default UserModalNew;
